import { useState } from "react";

import { Card, Button, Form, Input } from "antd";

import type {
  Gateway,
  GenerateGatewayClientCertificateResponse,
} from "@chirpstack/chirpstack-api-grpc-web/api/gateway_pb";
import { GenerateGatewayClientCertificateRequest } from "@chirpstack/chirpstack-api-grpc-web/api/gateway_pb";
import GatewayStore from "../../stores/GatewayStore";

import { useFormatDisplayTimestamp } from "../../contexts/DisplayDateTimeContext";

interface IProps {
  gateway: Gateway;
}

function GatewayCertificate(props: IProps) {
  const formatTs = useFormatDisplayTimestamp();
  const [certificate, setCertificate] = useState<GenerateGatewayClientCertificateResponse | undefined>(undefined);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

  const requestCertificate = () => {
    setButtonDisabled(true);

    const req = new GenerateGatewayClientCertificateRequest();
    req.setGatewayId(props.gateway.getGatewayId());

    GatewayStore.generateClientCertificate(req, (resp: GenerateGatewayClientCertificateResponse) => {
      setCertificate(resp);
    });
  };

  const renderRequest = () => {
    return (
      <Card>
        <p>
          The gateway client-certificate can be used to connect the gateway in case a client-certificate is required for
          authentication. Please note that this feature might not be available in case it has not been configured in the
          server configuration. After the certificate has been generated, store the content of the files on your
          gateway.
          <strong>
            {" "}
            Please note the expiration of the certificate and make sure to renew the certificate on time!
          </strong>
        </p>
        <p>
          <Button onClick={requestCertificate} disabled={buttonDisabled}>
            Generate certificate
          </Button>
        </p>
      </Card>
    );
  };

  const renderResponse = () => {
    const cert = certificate!;

    const initial = {
      expiresAt: formatTs(cert.getExpiresAt()!.toDate()!),
      caCert: cert.getCaCert(),
      tlsCert: cert.getTlsCert(),
      tlsKey: cert.getTlsKey(),
    };

    return (
      <Form layout="vertical" initialValues={initial}>
        <p>
          Install these on your Basic Station gateway's LNS credentials. The mapping is:
        </p>
        <ul>
          <li>
            <strong>TLS certificate</strong> &rarr; <code>tc.crt</code> (the gateway's client certificate)
          </li>
          <li>
            <strong>TLS key</strong> &rarr; <code>tc.key</code> (the gateway's private key)
          </li>
          <li>
            <strong>Trust file</strong> (<code>tc.trust</code>) &rarr; the <strong>public Let's Encrypt / ISRG
            root chain</strong>, the same for every gateway. The "CA certificate" shown below is NOT the
            trust file; see its note.
          </li>
        </ul>
        <p>
          Set the gateway's LNS URI to <code>wss://&lt;server&gt;:&lt;port&gt;</code> (US915 3001, EU868 3002,
          AU915 3003). <strong>Note the expiration and renew before it lapses.</strong>
        </p>
        <Form.Item
          label="Expiration date"
          name="expiresAt"
          tooltip="The certificate expires at this date. Make sure to generate and configure a new certificate for your gateway before this expiration date."
        >
          <Input disabled style={{ cursor: "text", color: "black" }} />
        </Form.Item>
        <Form.Item
          label="CA certificate"
          name="caCert"
          tooltip="This is the network server's gateway CA; the server uses it to verify your gateway's client certificate. It is NOT your gateway's tc.trust. For tc.trust use the public Let's Encrypt / ISRG root chain that signs the server's TLS certificate."
        >
          <Input.TextArea autoSize disabled style={{ cursor: "text", fontFamily: "monospace", color: "black" }} />
        </Form.Item>
        <Form.Item
          label="TLS certificate"
          name="tlsCert"
          tooltip="Your gateway's client certificate. Store this as a text-file on your gateway, e.g. named 'tc.crt'."
        >
          <Input.TextArea autoSize disabled style={{ cursor: "text", fontFamily: "monospace", color: "black" }} />
        </Form.Item>
        <Form.Item
          label="TLS key"
          name="tlsKey"
          tooltip="Your gateway's private key. Store this as a text-file on your gateway, e.g. named 'tc.key'."
        >
          <Input.TextArea autoSize disabled style={{ cursor: "text", fontFamily: "monospace", color: "black" }} />
        </Form.Item>
      </Form>
    );
  };

  if (certificate !== undefined) {
    return renderResponse();
  }

  return renderRequest();
}

export default GatewayCertificate;
